import { useEffect, useRef, useState } from "react";

// Shared audio hook — single audio element with play/pause state
export default function useSharedAudio(src, autoPlay = false) {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState(!src);

  if (typeof window !== "undefined" && !audioRef.current && src) {
    audioRef.current = new Audio(src);
    audioRef.current.preload = "none";
  }

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnd = () => setIsPlaying(false);
    const onErr = () => {
      setError(true);
      setIsPlaying(false);
    };
    audio.addEventListener("ended", onEnd);
    audio.addEventListener("error", onErr);
    return () => {
      audio.removeEventListener("ended", onEnd);
      audio.removeEventListener("error", onErr);
      audio.pause();
    };
  }, []);

  useEffect(() => {
    if (!autoPlay || !audioRef.current || error) return;
    const audio = audioRef.current;

    // First try direct autoplay (might work on revisits when user has interacted before).
    audio
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => {
        // Autoplay blocked — start on first user gesture anywhere on the page.
        const startOnGesture = () => {
          audio
            .play()
            .then(() => setIsPlaying(true))
            .catch(() => {});
          window.removeEventListener("pointerdown", startOnGesture);
          window.removeEventListener("keydown", startOnGesture);
          window.removeEventListener("touchstart", startOnGesture);
          window.removeEventListener("scroll", startOnGesture);
        };
        window.addEventListener("pointerdown", startOnGesture, { once: false });
        window.addEventListener("keydown", startOnGesture, { once: false });
        window.addEventListener("touchstart", startOnGesture, { once: false });
        window.addEventListener("scroll", startOnGesture, { once: false, passive: true });
      });
  }, [autoPlay, error]);

  const play = () => {
    if (!audioRef.current || error) return;
    audioRef.current
      .play()
      .then(() => setIsPlaying(true))
      .catch(() => {
        setError(true);
        setIsPlaying(false);
      });
  };
  const pause = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setIsPlaying(false);
  };
  const stop = () => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    setIsPlaying(false);
  };
  const seek = (delta) => {
    if (!audioRef.current) return;
    audioRef.current.currentTime = Math.max(
      0,
      audioRef.current.currentTime + delta,
    );
  };
  const toggle = () => (isPlaying ? pause() : play());
  const download = (downloadName) => {
    if (!src) return;
    const a = document.createElement("a");
    a.href = src;
    a.download = downloadName || "song.mp3";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return { isPlaying, error, play, pause, stop, seek, toggle, download, src };
}
